/**
 * Google Analytics 4 utility — thin wrapper around gtag()
 *
 * Usage:
 *   import { trackEvent, trackPageView } from '../utils/analytics';
 *   trackPageView('/naperville', 'Naperville — Happy Hours & Events');
 *   trackEvent('market_selected', { market: 'otc-naperville' });
 */

const GA_ID = 'G-V6899T3KLF';

function gtag() {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag.apply(null, arguments);
  }
}

/* ── Page Views (SPA-aware) ─────────────────────────────── */

export function trackPageView(path, title) {
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.origin + path,
    send_to: GA_ID,
  });
}

/* ── Custom Events ──────────────────────────────────────── */

export function trackEvent(eventName, params = {}) {
  gtag('event', eventName, params);
}

/* ── Pre-built event helpers ────────────────────────────── */

/** User picks a market on the landing page */
export function trackMarketSelected(marketId, marketLabel) {
  trackEvent('market_selected', {
    market_id: marketId,
    market_label: marketLabel,
  });
}

/** User switches market via the region bar dropdown */
export function trackMarketSwitched(fromMarket, toMarket) {
  trackEvent('market_switched', {
    from_market: fromMarket,
    to_market: toMarket,
  });
}

/** User expands a venue card to see deals */
export function trackVenueExpanded(venueName, marketId) {
  trackEvent('venue_expanded', {
    venue_name: venueName,
    market_id: marketId,
  });
}

/** User clicks on a deal/special */
export function trackDealClick(dealType, venueName, marketId) {
  trackEvent('deal_click', {
    deal_type: dealType,       // 'happy_hour', 'special', 'event'
    venue_name: venueName,
    market_id: marketId,
  });
}

/** User taps an event in the carousel */
export function trackEventClick(eventName, venueName, marketId) {
  trackEvent('event_click', {
    event_name: eventName,
    venue_name: venueName,
    market_id: marketId,
  });
}

/** User applies filters */
export function trackFilterApplied(filterType, values, marketId) {
  trackEvent('filter_applied', {
    filter_type: filterType,   // 'when', 'what', 'where'
    filter_values: values.join(','),
    market_id: marketId,
  });
}

/** User searches for a venue */
export function trackSearch(query, resultCount, marketId) {
  trackEvent('search', {
    search_term: query,
    result_count: resultCount,
    market_id: marketId,
  });
}

/** User opens the hamburger menu */
export function trackMenuOpen() {
  trackEvent('menu_open');
}

/** User submits a form from hamburger menu */
export function trackFormSubmit(formType) {
  trackEvent('form_submit', {
    form_type: formType,  // 'venue_submission', 'venue_claim', 'ad_inquiry', 'contact'
  });
}

/** User clicks "Add to Calendar" */
export function trackAddToCalendar(eventName, venueName) {
  trackEvent('add_to_calendar', {
    event_name: eventName,
    venue_name: venueName,
  });
}
